import { Card } from "@/components/ui/card";
import { classNames, platformLabel } from "@/lib/format";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia } from "@/lib/media";
import { CalendarDays, Crown, Users, TrendingUp, ArrowUpRight, ArrowDownRight, Minus, ClipboardCheck } from "lucide-react";

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "?";
const PLATFORM_COLOR = { tiktok: "bg-foreground", instagram: "bg-[hsl(var(--rose))]", youtube: "bg-[hsl(var(--amber))]" };

function Head({ icon: Icon, label, hint }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold"><Icon className="h-3 w-3" />{label}</div>
      {hint && <span className="text-[10px] text-muted-foreground truncate">{hint}</span>}
    </div>
  );
}

// Members carry no avatar field; creator rows always pass one, possibly empty or failing.
function RankRow({ active, onClick, avatar, demo = false, name, sub, count, share, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={classNames("w-full flex items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-accent", active && "bg-primary/10")}>
      {avatar !== undefined ? <MediaImage src={avatar} alt="" name={name} kind="avatar" demo={demo} className="h-6 w-6 rounded-full object-cover shrink-0" /> : <span className="h-6 w-6 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center shrink-0">{initials(name)}</span>}
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-medium truncate">{name}</span>
        {sub && <span className="block text-[10px] text-muted-foreground truncate">{sub}</span>}
      </span>
      <span className="font-mono text-xs font-bold">{count}</span>
      <span className="font-mono text-[10px] text-muted-foreground w-8 text-right">{share}%</span>
    </button>
  );
}

export default function DecisionSummary({ summary, filters, onFilter }) {
  if (!summary) return null;
  const delta = summary.vs_last_month;
  const DeltaIcon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const max = Math.max(1, ...summary.weekly.map((w) => w.count));
  const fu = summary.follow_ups || { open: 0, overdue: 0, due_soon: 0, done: 0, done_this_month: 0, unassigned: 0 };
  const fuStat = (key, value, label, color) => (
    <button onClick={() => onFilter("status", filters.status === key ? "all" : key)} data-testid={`summary-follow-up-${key}`}
      className={classNames("flex flex-col items-start rounded-md px-2 py-1 text-left transition-colors hover:bg-accent", filters.status === key && "bg-primary/10")}>
      <span className={classNames("font-mono text-xl font-bold leading-none", color)}>{value}</span>
      <span className="text-[10px] text-muted-foreground mt-0.5">{label}</span>
    </button>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="decision-summary">
      <Card className="p-4 space-y-2" data-testid="summary-this-month">
        <Head icon={CalendarDays} label="This month" hint={summary.month_label} />
        <div className="flex items-end gap-2">
          <span className="font-mono text-3xl font-bold leading-none" data-testid="summary-this-month-count">{summary.this_month}</span>
          <span className={classNames("inline-flex items-center gap-0.5 text-xs font-semibold mb-0.5", delta > 0 ? "text-[hsl(var(--emerald))]" : delta < 0 ? "text-[hsl(var(--rose))]" : "text-muted-foreground")} data-testid="summary-delta">
            <DeltaIcon className="h-3.5 w-3.5" />{delta > 0 ? `+${delta}` : delta} vs {summary.prev_month_label}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground">{summary.total} decision{summary.total === 1 ? "" : "s"} in view · {summary.last_month} last month</div>
      </Card>

      <Card className="p-3 space-y-1" data-testid="summary-top-creators">
        <div className="px-1.5 pt-1"><Head icon={Crown} label="Top creators" hint="click to filter" /></div>
        {summary.top_creators.length === 0 ? <div className="text-xs text-muted-foreground px-1.5 py-2">No creators in view</div> : summary.top_creators.map((c) => (
          <RankRow key={c.id} testid={`summary-creator-${c.id}`} active={filters.creator === c.id} onClick={() => onFilter("creator", filters.creator === c.id ? "all" : c.id)}
            avatar={c.avatar ?? ""} demo={explicitDemoMedia(c)} name={c.name} sub={`${c.handle} · ${platformLabel(c.platform)}`} count={c.count} share={c.share_pct} />
        ))}
      </Card>

      <Card className="p-3 space-y-1" data-testid="summary-top-members">
        <div className="px-1.5 pt-1"><Head icon={Users} label="Most active" hint="decision-makers" /></div>
        {summary.top_members.length === 0 ? <div className="text-xs text-muted-foreground px-1.5 py-2">No decision-makers in view</div> : summary.top_members.map((m) => (
          <RankRow key={m.id} testid={`summary-member-${m.id}`} active={filters.member === m.id} onClick={() => onFilter("member", filters.member === m.id ? "all" : m.id)}
            name={m.name} count={m.count} share={m.share_pct} />
        ))}
      </Card>

      <Card className="p-4 space-y-2" data-testid="summary-trend">
        <Head icon={TrendingUp} label="12-week trend" hint={`${summary.weekly_total} in 12 wks`} />
        <div className="flex items-end gap-1 h-12" data-testid="summary-sparkline">
          {summary.weekly.map((w, i) => (
            <div key={w.week_start} title={`Week of ${w.week_start}: ${w.count}`} className="flex-1 flex items-end h-full">
              <div className={classNames("w-full rounded-sm transition-all", i === summary.weekly.length - 1 ? "bg-[hsl(var(--amber))]" : "bg-primary/40")} style={{ height: `${Math.max(w.count / max * 100, w.count ? 12 : 4)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex h-1.5 rounded-full overflow-hidden bg-muted" data-testid="summary-platform-bar">
          {summary.platforms.map((p) => <div key={p.platform} className={PLATFORM_COLOR[p.platform] || "bg-primary"} style={{ width: `${p.share_pct}%` }} title={`${platformLabel(p.platform)} ${p.share_pct}%`} />)}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
          {summary.platforms.map((p) => (
            <button key={p.platform} onClick={() => onFilter("platform", filters.platform === p.platform ? "all" : p.platform)} className={classNames("inline-flex items-center gap-1 hover:text-foreground", filters.platform === p.platform && "text-foreground font-semibold")} data-testid={`summary-platform-${p.platform}`}>
              <span className={classNames("h-1.5 w-1.5 rounded-full", PLATFORM_COLOR[p.platform] || "bg-primary")} />{platformLabel(p.platform)} {p.share_pct}%
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4 space-y-2" data-testid="summary-follow-ups">
        <Head icon={ClipboardCheck} label="Follow-ups" hint="click to filter" />
        <div className="grid grid-cols-3 gap-1">
          {fuStat("open", fu.open, "open", "text-foreground")}
          {fuStat("overdue", fu.overdue, "overdue", fu.overdue > 0 ? "text-[hsl(var(--rose))]" : "text-muted-foreground")}
          {fuStat("done", fu.done_this_month, "done this month", "text-[hsl(var(--emerald))]")}
        </div>
        <div className="text-[11px] text-muted-foreground">{fu.due_soon} due within 3 days · {fu.unassigned} unassigned</div>
      </Card>
    </div>
  );
}
